import { useState } from "react";
import { Plus } from "lucide-react";
import { useGlobalStacks } from "@/state/globalStacks";
import { HGRecordUrl } from "@/common/types";
import RecordSheet from "./RecordSheet";
import { useCollection, useIsOwnSpace } from "@/api/hooks";
import { HGStacks } from "@/stack/stacks";
import { RecordsMode } from "@/state/recordsView";
import {
  CONTENT_R,
  CONTENT_X,
  ROW_PAD_Y,
  SPINE_X,
  TITLE_CY,
} from "@/common/timelineGeometry";
import { Spine } from "./TimelineRail";

function getEmptyRecord(
  collectionIDs: string[],
  stacks: HGStacks,
  withIntervals: boolean,
) {
  return {
    title: "",
    content: "",
    collection_id: collectionIDs,
    url: [] as HGRecordUrl[],
    interval: withIntervals ? stacks.getInterval() : undefined,
    end_interval: withIntervals
      ? stacks.getEndInterval() || stacks.getInterval()
      : undefined,
  };
}

interface RecordNewProps {
  /** Which kind of record to create; mirrors the band's active tab. */
  mode: RecordsMode;
}

/**
 * The record that is not there yet.
 *
 * Drawn as a row of the list rather than as a control above it: the same card,
 * on the same rail, at the same inset, carrying the same serif title line — but
 * outlined in dashes and with a `+` where its marker would be. Pressing it opens
 * the form, and what comes back takes exactly the place the outline was holding,
 * which is the whole point of drawing it this way. A 30px glyph among the view
 * controls said none of that.
 *
 * On the timeline it also names the range the record will be filed under, which
 * is the one thing about the outcome that is not visible from where the row
 * sits — and the reason the row belongs on the time axis at all.
 *
 * Above the list rather than in it, and outside the scroll container, so it
 * reads the same on both tabs and stays reachable however far down the list the
 * user has gone. Its `mode` decides only whether the new record is seeded with
 * the current interval; a note is a record with no place on the time axis, and
 * nothing about writing one should look different.
 *
 * Renders nothing in someone else's space, so the row is never an affordance
 * that only 404s.
 */
export default function RecordNew({ mode }: RecordNewProps) {
  const collection = useCollection();
  const globalStacks = useGlobalStacks();
  const isOwnSpace = useIsOwnSpace();
  const [open, setOpen] = useState(false);

  // Someone else's page is read-only: the record would have to go into one of
  // their collections, which the API rejects.
  if (!isOwnSpace) return null;

  const collectionIDs = collection ? [collection.id] : [];
  const label = mode === "notes" ? "New note" : "New record";
  // A note has no place on the time axis, so there is no rail to sit on and no
  // range to name: the card takes the notes list's own inset instead.
  const onAxis = mode === "time";
  const where = onAxis ? globalStacks.getName() : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={where ? `${label} in ${where}` : label}
        className="group relative block w-full shrink-0 bg-background text-left"
        style={{
          // The timeline's 6px lead-in is inside the scroller, below this row,
          // so the air above the first card is carried here instead.
          paddingTop: ROW_PAD_Y + 6,
          paddingBottom: ROW_PAD_Y,
          paddingLeft: onAxis ? CONTENT_X : 16,
          paddingRight: onAxis ? CONTENT_R : 16,
        }}
      >
        {onAxis && (
          <>
            {/* The axis is not hypothetical — only the record is. So the spine
                runs solid through this row and joins the one below it, and the
                dashes are spent on the card and its marker. */}
            <Spine depth={0} />

            {/* The card stops short of the spine; the stub carries the mark to
                it, dashed like everything else this row is promising. */}
            <span
              aria-hidden
              className="pointer-events-none absolute border-t border-dashed border-node-line"
              style={{
                left: SPINE_X,
                top: TITLE_CY + 6,
                width: CONTENT_X - SPINE_X,
              }}
            />

            {/* Where the record's own marker will be. */}
            <span
              aria-hidden
              className="absolute flex items-center justify-center rounded-full border border-dashed border-primary/50 bg-background text-primary/70 transition-colors group-hover:border-primary group-hover:text-primary"
              style={{
                left: SPINE_X - 8,
                top: TITLE_CY - 8 + 6,
                width: 16,
                height: 16,
              }}
            >
              <Plus className="size-[9px]" strokeWidth={3} />
            </span>
          </>
        )}

        <div className="flex items-center gap-3 rounded-lg border border-dashed border-border px-[14px] py-[11px] transition-colors group-hover:border-primary/60 group-hover:bg-row-hover">
          {/* Same face, size and leading as a record's title, so the outline is
              read as the row it will become and not as a button. */}
          <span className="min-w-0 flex-1 truncate font-serif text-[14.5px] font-semibold leading-[1.28] text-muted-foreground transition-colors group-hover:text-foreground">
            {label}
          </span>
          {where ? (
            // Where a record card carries its collection badge. Held at zero
            // opacity until the row is hovered — the range is a confirmation of
            // what pressing the row will do, not something to read at rest, and
            // the reserved width keeps the title from reflowing when it fades in.
            <span className="max-w-[45%] shrink-0 truncate font-mono text-[10.5px] text-ink2 opacity-0 transition-opacity group-hover:opacity-100">
              {where}
            </span>
          ) : (
            <Plus className="size-[13px] shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
          )}
        </div>
      </button>

      <RecordSheet
        record={getEmptyRecord(collectionIDs, globalStacks, mode === "time")}
        title={label}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}
